import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/Table";
import { Input } from "src/components/ui/Input";
import { Button } from "src/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "src/components/ui/DropdownMenu";
import { Badge } from "src/components/ui/Badge";
import { Checkbox } from "src/components/ui/Checkbox";
import {
  Search,
  Filter,
  ArrowUpDown,
  Columns,
  Clipboard,
  Check,
  Download,
  AlertTriangle,
  FileText,
  Info,
  Calendar,
  BarChart3,
  MessageSquare,
  Star,
  Clock,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { formatDate } from "src/lib/utils";
import { useProfileData } from "src/hooks/useProfileData";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/Tooltip";
import { useSessions } from "src/hooks/useSessions";
import { EmptyState } from "src/components/ui/EmptyState";
import LoadingSpinner from "src/components/LoadingSpinner";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { formatSessionTime, getTutorDisplayName, getTopicName, getSubtopicName, getTutorPhoto, getTutorRating, generateSessionId, getSubjectName } from "src/components/student/SessionCard";

// Define interfaces for real data
interface SessionParticipant {
  name: string;
  role: "Instructor" | "Student";
  joined: string;
  left: string;
  attendance: "Attended" | "Late" | "Missed" | "Left Early";
  missedReason?: string;
}

const StudentPastSessions = () => {
  // Use real sessions hook
  const {
    pastSessions,
    isLoading: isLoadingSessions,
    error: sessionsError,
    refreshSessions
  } = useSessions();

  // Get profile data for the UserNavbar
  const profileData = useProfileData();

  // Page header store
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());

  // Set up page header
  useEffect(() => {
    setTitle("Session History");
    setDescription("Review your completed learning sessions and track your progress");
    return () => reset();
  }, [setTitle, setDescription, reset]);
  
  // Column visibility state - keeping all original columns
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    externalId: false, // Not available in real data, defaulting to false
    tutor: true,
    country: false, // Not available in real data, defaulting to false
    subject: true,
    topic: true,
    subtopic: true,
    sessionType: true,
    status: true,
    date: true,
    time: true,
    duration: true,
    participation: true, // Now available from session_details
    participationScore: true, // Now available from session_details
    feedbackRating: true,
    actions: true,
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: [] as string[],
    sessionType: [] as string[],
    subject: [] as string[],
  });

  // Local state for sorting
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Get unique filter options from real data
  const getFilterOptions = () => {
    const statuses = [...new Set(pastSessions.map(s => s.status))];
    const sessionTypes = [...new Set(pastSessions.map(s => s.session_type))];
    const subjects = [...new Set(pastSessions.map(s => getSubjectName(s.curriculum_unit)).filter(Boolean))];

    return { statuses, sessionTypes, subjects };
  };

  const { statuses, sessionTypes, subjects } = getFilterOptions();

  // Filter sessions based on search term and filters
  const filteredSessions = useMemo(() => {
    return pastSessions.filter(session => {
      // Search term filter
      if (searchTerm) {
        const tutorName = getTutorDisplayName(session.tutor_profile, session.tutor_data).toLowerCase();
        const topicName = getTopicName(session.curriculum_unit).toLowerCase();
        const batchName = session.batch?.name?.toLowerCase() || '';
        const sessionId = generateSessionId(session.id).toLowerCase();

        const matchesSearch = tutorName.includes(searchTerm.toLowerCase()) ||
                             topicName.includes(searchTerm.toLowerCase()) ||
                             batchName.includes(searchTerm.toLowerCase()) ||
                             sessionId.includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(session.status)) {
        return false;
      }

      // Session type filter
      if (filters.sessionType.length > 0 && !filters.sessionType.includes(session.session_type)) {
        return false;
      }

      // Subject filter
      if (filters.subject.length > 0) {
        const sessionSubject = getSubjectName(session.curriculum_unit);
        if (!filters.subject.includes(sessionSubject)) {
          return false;
        }
      }

      return true;
    });
  }, [pastSessions, searchTerm, filters]);

  // Sort filtered sessions
  const sortedSessions = useMemo(() => {
    if (!sortConfig) return filteredSessions;

    return [...filteredSessions].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'date':
          aValue = new Date(a.scheduled_at);
          bValue = new Date(b.scheduled_at);
          break;
        case 'tutor':
          aValue = getTutorDisplayName(a.tutor_profile, a.tutor_data);
          bValue = getTutorDisplayName(b.tutor_profile, b.tutor_data);
          break;
        case 'topic':
          aValue = getTopicName(a.curriculum_unit);
          bValue = getTopicName(b.curriculum_unit);
          break;
        default:
          aValue = a[sortConfig.key as keyof typeof a];
          bValue = b[sortConfig.key as keyof typeof b];
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredSessions, sortConfig]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      "completed": { color: "bg-green-100 text-green-800", icon: <Check size={12} /> },
      "cancelled": { color: "bg-red-100 text-red-800", icon: <AlertTriangle size={12} /> },
      "rescheduled": { color: "bg-orange-100 text-orange-800", icon: <Clock size={12} /> },
      "scheduled": { color: "bg-blue-100 text-blue-800", icon: <Clock size={12} /> },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { 
      color: "bg-gray-100 text-gray-800", 
      icon: null 
    };

    return (
      <Badge className={`${config.color} flex items-center gap-1 capitalize`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  const getParticipationBadge = (level: string, score: number) => {
    const levelConfig = {
      "Highly Engaged": "bg-green-100 text-green-800",
      "Engaged": "bg-blue-100 text-blue-800",
      "Moderately Engaged": "bg-yellow-100 text-yellow-800",
      "Low Engagement": "bg-orange-100 text-orange-800",
      "Not Attended": "bg-red-100 text-red-800",
    };

    const color = levelConfig[level as keyof typeof levelConfig] || "bg-gray-100 text-gray-800";

    return (
      <div className="flex flex-col items-center">
        <Badge className={color}>
          {level}
        </Badge>
        {score > 0 && (
          <span className="text-xs text-gray-500 mt-1">{score}/10</span>
        )}
      </div>
    );
  };

  const getRatingStars = (rating: number) => {
    if (!rating || rating === 0) return <span className="text-gray-400">No rating</span>;

    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
          />
        ))}
        <span className="text-sm text-gray-600 ml-1">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const getAttendanceBadge = (attendance: string, missedReason?: string) => {
    const attendanceConfig = {
      "Attended": { color: "bg-green-100 text-green-800", icon: <Check size={12} /> },
      "Late": { color: "bg-yellow-100 text-yellow-800", icon: <Clock size={12} /> },
      "Missed": { color: "bg-red-100 text-red-800", icon: <AlertTriangle size={12} /> },
      "Left Early": { color: "bg-orange-100 text-orange-800", icon: <Clock size={12} /> },
    };

    const config = attendanceConfig[attendance as keyof typeof attendanceConfig] || {
      color: "bg-gray-100 text-gray-800",
      icon: null
    };

    // If there's a missed reason, wrap in tooltip
    if (missedReason) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                className={`${config.color} flex items-center gap-1 text-xs cursor-pointer`}
                title={missedReason} // Fallback native tooltip
              >
                {config.icon}
                {attendance}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p>{missedReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Badge className={`${config.color} flex items-center gap-1 text-xs`}>
        {config.icon}
        {attendance}
      </Badge>
    );
  };

  const renderParticipationCells = (participants: SessionParticipant[], columnIndex: number) => {
    return (
      <div className="space-y-1">
        {participants.map((participant, index) => (
          <div key={index} className="text-xs py-1 border-b border-gray-100 last:border-b-0">
            {columnIndex === 0 && (
              <span className="font-medium text-gray-600">
                {participant.role === "Instructor" ? "Tutor" : "Student"}
              </span>
            )}
            {columnIndex === 1 && (
              <span className="truncate" title={participant.name}>
                {participant.name}
              </span>
            )}
            {columnIndex === 2 && (
              <span className="text-gray-500 whitespace-nowrap">
                {participant.joined || "—"}
              </span>
            )}
            {columnIndex === 3 && (
              <span className="text-gray-500 whitespace-nowrap">
                {participant.left || "—"}
              </span>
            )}
            {columnIndex === 4 && (
              <div>
                {getAttendanceBadge(participant.attendance, participant.missedReason)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const handleSelectAll = () => {
    if (selectedSessions.size === sortedSessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sortedSessions.map(s => s.id)));
    }
  };

  const toggleSessionSelection = (sessionId: string) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const clearSelection = () => {
    setSelectedSessions(new Set());
  };

  const isAllSelected = selectedSessions.size === sortedSessions.length && sortedSessions.length > 0;

  // Filter toggle functions
  const toggleFilter = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      sessionType: [],
      subject: [],
    });
  };

  const hasActiveFilters = filters.status.length > 0 || filters.sessionType.length > 0 || filters.subject.length > 0;

  // Helper function to get participation data from session_details
  const getParticipationData = (session: any) => {
    const participants: SessionParticipant[] = [];
    
    if (session.tutor_profile) {
      const tutorJoined = session.session_details?.tutor_joined_at;
      const tutorLeft = session.session_details?.tutor_left_at;
      
      participants.push({
        name: getTutorDisplayName(session.tutor_profile, session.tutor_data),
        role: "Instructor",
        joined: tutorJoined ? new Date(tutorJoined).toLocaleTimeString() : "—",
        left: tutorLeft ? new Date(tutorLeft).toLocaleTimeString() : "—",
        attendance: tutorJoined ? "Attended" : "Missed"
      });
    }

    if (session.student_profile) {
      const studentJoined = session.session_details?.student_joined_at;
      const studentLeft = session.session_details?.student_left_at;
      
      participants.push({
        name: `${session.student_profile.first_name} ${session.student_profile.last_name}`,
        role: "Student",
        joined: studentJoined ? new Date(studentJoined).toLocaleTimeString() : "—",
        left: studentLeft ? new Date(studentLeft).toLocaleTimeString() : "—",
        attendance: studentJoined ? "Attended" : "Missed"
      });
    }

    return participants;
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
          <div className="relative flex-grow max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by tutor, subject, curriculum unit, or notes"
              className="pl-10 pr-4 py-2 w-full border-gray-200 rounded-md focus:border-rfpurple-500 focus:ring focus:ring-rfpurple-200 focus:ring-opacity-50 transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Filter size={16} />
                  Filter
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                      {filters.status.length + filters.sessionType.length + filters.subject.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="p-2 text-sm font-medium text-gray-700 border-b">
                  Filter Options
                </div>

                {/* Status Filter */}
                <div className="p-2">
                  <div className="text-xs font-medium text-gray-600 mb-2">Status</div>
                  {statuses.map(status => (
                    <DropdownMenuCheckboxItem
                      key={status}
                      checked={filters.status.includes(status)}
                      onCheckedChange={() => toggleFilter('status', status)}
                      className="capitalize"
                    >
                      {status}
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>

                {/* Session Type Filter */}
                <div className="p-2 border-t">
                  <div className="text-xs font-medium text-gray-600 mb-2">Session Type</div>
                  {sessionTypes.map(type => (
                    <DropdownMenuCheckboxItem
                      key={type}
                      checked={filters.sessionType.includes(type)}
                      onCheckedChange={() => toggleFilter('sessionType', type)}
                      className="capitalize"
                    >
                      {type}
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>

                {/* Subject Filter */}
                <div className="p-2 border-t">
                  <div className="text-xs font-medium text-gray-600 mb-2">Subject</div>
                  {subjects.map(subject => (
                    <DropdownMenuCheckboxItem
                      key={subject}
                      checked={filters.subject.includes(subject)}
                      onCheckedChange={() => toggleFilter('subject', subject)}
                    >
                      {subject}
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>

                {hasActiveFilters && (
                  <div className="p-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                      className="w-full"
                    >
                      Clear All Filters
                    </Button>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Columns size={16} />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="p-2 text-sm font-medium text-gray-700 border-b">
                  Toggle Columns
                </div>
                {Object.entries(visibleColumns).map(([key, value]) => (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={value}
                    onCheckedChange={(checked) =>
                      setVisibleColumns(prev => ({ ...prev, [key]: checked }))
                    }
                    className="capitalize"
                  >
                    {key === "participationScore" ? "Participation Score" :
                     key === "feedbackRating" ? "Rating" :
                     key === "participation" ? "Participation Details" : key}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {selectedSessions.size > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Download size={16} />
                  Export ({selectedSessions.size})
                </Button>
                <Button variant="outline" size="sm" onClick={clearSelection}>
                  Clear Selection
                </Button>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={refreshSessions}
              className="flex items-center gap-2"
              disabled={isLoadingSessions}
            >
              <RefreshCw size={16} className={isLoadingSessions ? "animate-spin" : ""} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Content Area */}
        {isLoadingSessions ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
            <span className="ml-2">Loading past sessions...</span>
          </div>
        ) : sessionsError ? (
          <div className="text-center py-6">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{sessionsError}</p>
            <Button onClick={refreshSessions} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : sortedSessions.length === 0 ? (
          <EmptyState
            title="No past sessions"
            description={pastSessions.length === 0 
              ? "You haven't completed any sessions yet."
              : "No sessions match your search criteria."}
            svgPath="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
            actionText="Book Your First Session"
            onActionClick={() => window.location.href = "/student/request-booking"}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* First row - Main headers */}
                <TableRow>
                  <TableHead className="w-12" rowSpan={2}>
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.id && (
                    <TableHead className="cursor-pointer" rowSpan={2} onClick={() => handleSort('id')}>
                      <div className="flex items-center gap-1">
                        Session ID
                        <ArrowUpDown size={14} />
                      </div>
                    </TableHead>
                  )}
                  {visibleColumns.externalId && <TableHead rowSpan={2}>External ID</TableHead>}
                  {visibleColumns.tutor && (
                    <TableHead className="cursor-pointer" rowSpan={2} onClick={() => handleSort('tutor')}>
                      <div className="flex items-center gap-1">
                        Tutor
                        <ArrowUpDown size={14} />
                      </div>
                    </TableHead>
                  )}
                  {visibleColumns.country && <TableHead rowSpan={2}>Country</TableHead>}
                  {visibleColumns.subject && <TableHead rowSpan={2}>Subject</TableHead>}
                  {visibleColumns.topic && (
                    <TableHead className="cursor-pointer" rowSpan={2} onClick={() => handleSort('topic')}>
                      <div className="flex items-center gap-1">
                        Unit
                        <ArrowUpDown size={14} />
                      </div>
                    </TableHead>
                  )}
                  {visibleColumns.subtopic && <TableHead rowSpan={2}>Learning objective</TableHead>}
                  {visibleColumns.sessionType && <TableHead rowSpan={2}>Type</TableHead>}
                  {visibleColumns.status && <TableHead rowSpan={2}>Status</TableHead>}
                  {visibleColumns.date && (
                    <TableHead className="cursor-pointer" rowSpan={2} onClick={() => handleSort('date')}>
                      <div className="flex items-center gap-1">
                        Date
                        <ArrowUpDown size={14} />
                      </div>
                    </TableHead>
                  )}
                  {visibleColumns.time && <TableHead rowSpan={2}>Time</TableHead>}
                  {visibleColumns.duration && <TableHead rowSpan={2}>Duration</TableHead>}
                  {visibleColumns.participation && (
                    <TableHead colSpan={5} className="text-center bg-gray-100 border-b-0">
                      <div className="text-sm font-medium text-gray-700">Participation</div>
                    </TableHead>
                  )}
                  {visibleColumns.participationScore && <TableHead rowSpan={2}>Participation Score</TableHead>}
                  {visibleColumns.feedbackRating && <TableHead rowSpan={2}>Rating</TableHead>}
                  {visibleColumns.actions && <TableHead rowSpan={2}>Actions</TableHead>}
                </TableRow>

                {/* Second row - Sub-headers for Participation */}
                {visibleColumns.participation && (
                  <TableRow>
                    <TableHead className="text-xs font-medium text-gray-600 bg-gray-50 w-16">ROLE</TableHead>
                    <TableHead className="text-xs font-medium text-gray-600 bg-gray-50 w-32">ATTENDEE</TableHead>
                    <TableHead className="text-xs font-medium text-gray-600 bg-gray-50 w-20">JOINED</TableHead>
                    <TableHead className="text-xs font-medium text-gray-600 bg-gray-50 w-20">LEFT</TableHead>
                    <TableHead className="text-xs font-medium text-gray-600 bg-gray-50 w-28">ATTENDANCE</TableHead>
                  </TableRow>
                )}
              </TableHeader>
              <TableBody>
                {sortedSessions.map((session) => {
                  const { date, time } = formatSessionTime(session.scheduled_at);
                  const tutorDisplayName = getTutorDisplayName(session.tutor_profile, session.tutor_data);
                  const tutorPhoto = getTutorPhoto(session.tutor_profile);
                  const topicName = getTopicName(session.curriculum_unit);
                  const subtopicName = getSubtopicName(session.curriculum_objective);
                  const tutorRating = getTutorRating(session.tutor_data);
                  const sessionId = generateSessionId(session.id);
                  const rating = session.session_feedback?.[0]?.rating || 0;
                  const participants = getParticipationData(session);

                  return (
                    <TableRow key={session.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSessions.has(session.id)}
                          onCheckedChange={() => toggleSessionSelection(session.id)}
                        />
                      </TableCell>
                      {visibleColumns.id && (
                        <TableCell className="font-medium">{sessionId}</TableCell>
                      )}
                      {visibleColumns.externalId && (
                        <TableCell>—</TableCell>
                      )}
                      {visibleColumns.tutor && (
                        <TableCell>
                          <div className="flex items-center">
                            <img
                              src={tutorPhoto}
                              alt={tutorDisplayName}
                              className="w-8 h-8 rounded-full object-cover mr-2"
                            />
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{tutorDisplayName}</span>
                                {tutorRating && (
                                  <div className="flex items-center">
                                    <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                                    <span className="text-xs text-gray-600">{tutorRating}</span>
                                  </div>
                                )}
                              </div>
                              {session.tutor_data?.education && (
                                <p className="text-xs text-gray-500">{session.tutor_data.education}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.country && (
                        <TableCell>—</TableCell>
                      )}
                      {visibleColumns.subject && (
                        <TableCell>
                          <Badge variant="outline">{getSubjectName(session.curriculum_unit)}</Badge>
                        </TableCell>
                      )}
                      {visibleColumns.topic && (
                        <TableCell>
                          <div>
                            <div className="font-medium">{topicName}</div>
                            {session.batch?.name && (
                              <p className="text-xs text-gray-500 mt-1">
                                {session.batch.name}
                              </p>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.subtopic && (
                        <TableCell>
                          {subtopicName || '—'}
                        </TableCell>
                      )}
                      {visibleColumns.sessionType && (
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {session.session_type}
                          </Badge>
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>{getStatusBadge(session.status)}</TableCell>
                      )}
                      {visibleColumns.date && (
                        <TableCell>{date}</TableCell>
                      )}
                      {visibleColumns.time && (
                        <TableCell>{time}</TableCell>
                      )}
                      {visibleColumns.duration && (
                        <TableCell>{session.duration_min} min</TableCell>
                      )}
                      {visibleColumns.participation && (
                        <>
                          <TableCell className="p-2 w-16">
                            {renderParticipationCells(participants, 0)}
                          </TableCell>
                          <TableCell className="p-2 w-32">
                            {renderParticipationCells(participants, 1)}
                          </TableCell>
                          <TableCell className="p-2 w-20">
                            {renderParticipationCells(participants, 2)}
                          </TableCell>
                          <TableCell className="p-2 w-20">
                            {renderParticipationCells(participants, 3)}
                          </TableCell>
                          <TableCell className="p-2 w-28">
                            {renderParticipationCells(participants, 4)}
                          </TableCell>
                        </>
                      )}
                      {visibleColumns.participationScore && (
                        <TableCell>
                          {session.session_details?.participation_score ? 
                            getParticipationBadge(
                              session.session_details.participation_level || "Engaged",
                              session.session_details.participation_score
                            ) : 
                            <span className="text-gray-400">—</span>
                          }
                        </TableCell>
                      )}
                      {visibleColumns.feedbackRating && (
                        <TableCell>{getRatingStars(rating)}</TableCell>
                      )}
                      {visibleColumns.actions && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <FileText size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MessageSquare size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <BarChart3 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
};

export default StudentPastSessions;
